import {
  getNotificationBus,
  NotificationBus,
} from '@pogokid/dblite'
import { Kysely } from '@pogokid/dblite/kysely'
import { EventStore } from './EventStore'
import { DomainEvent } from './DomainEvent'
import {
  DomainEventRef,
  DomainEventRefSyncState,
  EventSourcingDatabase,
  PullProgress,
  PullResponse,
  RemoteEventsStatus,
  TDomainEvent,
} from './types'
import { ISODateTimeString } from '@pogokid/util/datetime'
import { FetchResponse, fetchWithAuth } from '../fetch'
import { SingleResponse } from '../Response'
import { AppError } from '../AppError'
import {
  chunk,
  delay,
  delayJitter,
  isArrayWithLength,
  retry,
} from '@pogokid/util'
import { captureException, getAllLogger } from '@pogokid/log'

const log = getAllLogger('EventStoreSync')

interface PushRequestBody {
  last_seen_sequence_number: number | null
  events: Array<
    Omit<
      TDomainEvent,
      'sequence_number' | 'device_sequence_number'
    >
  >
}

interface PushResponse {
  committed_events: Array<{
    id: string
    sequence_number: number
    created_at: ISODateTimeString
  }>
}

export interface EventStoreSyncOptions {
  baseUrl: string
  getAccessToken: () => Promise<string | null>
  /**
   * Called during a non-live pull as each batch of events is
   * downloaded, so the UI can show sync progress
   */
  onPullProgress?: (progress: PullProgress) => void
  /**
   * Called once when the server rejects our access token.
   *
   * Automatic syncing is stopped at this point, so whoever
   * owns the tokens must call `resumeAfterReauthentication`
   * and restart syncing once the user has signed in again.
   */
  onUnauthorized?: (
    aggregateType: string,
    profileId: string
  ) => void
}

export class EventStoreSync {
  private baseUrl: string
  private liveSyncEnabled: boolean = false
  private isPushing = false
  /**
   * Set when the server rejected our access token. While this
   * is set we do not start any automatic syncing, because every
   * request would be rejected the same way.
   */
  private awaitingReauthentication: boolean = false
  private getAccessToken: () => Promise<string | null>
  private onPullProgress:
    | ((progress: PullProgress) => void)
    | undefined
  private onUnauthorized:
    | ((aggregateType: string, profileId: string) => void)
    | undefined
  private onDomainEvent: ((payload: string) => void) | undefined
  private notificationBus: NotificationBus
  private abortPull: AbortController | undefined

  constructor(
    private db: Kysely<EventSourcingDatabase>,
    private eventStore: EventStore,
    options: EventStoreSyncOptions
  ) {
    this.baseUrl = options.baseUrl
    this.getAccessToken = options.getAccessToken
    this.onPullProgress = options.onPullProgress
    this.onUnauthorized = options.onUnauthorized
    this.notificationBus = getNotificationBus()
  }

  static new(
    db: Kysely<EventSourcingDatabase>,
    eventStore: EventStore,
    options: EventStoreSyncOptions
  ): EventStoreSync {
    return new EventStoreSync(db, eventStore, options)
  }

  /**
   * Pull events from the server for a specific aggregate/profile
   */
  private async pullEvents(
    aggregateType: string,
    profileId: string,
    cursor: number = -1,
    limit: number = 50,
    live: boolean = false
  ): Promise<
    FetchResponse<
      PullResponse & { events: DomainEvent[] | null }
    >
  > {
    const accessToken = await this.getAccessToken()
    if (!accessToken) {
      const err = new AppError('No access token available')
      err.code = 'no_access_token'
      err.status = 401
      throw err
    }

    const searchParams = new URLSearchParams({
      cursor: cursor.toString(),
      limit: limit.toString(),
      live: live.toString(),
    })

    // Abort any previous requests
    if (this.abortPull) {
      this.abortPull.abort()
    }
    this.abortPull = new AbortController()

    const response = await fetchWithAuth<PullResponse>({
      baseUrl: this.baseUrl,
      accessToken,
      method: 'get',
      endpoint: `/v1/events/${aggregateType}/${profileId}/pull`,
      searchParams,
      signal: this.abortPull?.signal,
    })

    return {
      ...response,
      events: response.data?.map(DomainEvent.fromData),
    }
  }

  /**
   * Fetch the server-side event status for an aggregate/profile.
   * Returns null when the status could not be fetched — progress
   * reporting is best-effort and must never break a sync.
   */
  async fetchRemoteStatus(
    aggregateType: string,
    profileId: string
  ): Promise<RemoteEventsStatus | null> {
    try {
      const accessToken = await this.getAccessToken()
      if (!accessToken) return null

      // The status endpoint returns a bare object, not a
      // `{ data }` envelope, so its fields sit alongside the
      // fetch metadata on the response.
      const response = await fetchWithAuth<
        RemoteEventsStatus & { data: null }
      >({
        baseUrl: this.baseUrl,
        accessToken,
        method: 'get',
        endpoint: `/v1/events/${aggregateType}/${profileId}/status`,
      })

      if (
        response.error ||
        typeof response.latest_sequence_number !== 'number'
      ) {
        return null
      }

      return {
        latest_sequence_number: response.latest_sequence_number,
        latest_timestamp: response.latest_timestamp ?? null,
        last_processed_sequence:
          response.last_processed_sequence,
        projections_updated_at:
          response.projections_updated_at ?? null,
      }
    } catch (error) {
      log.warn('Failed to fetch remote event status', error)
      return null
    }
  }

  /**
   * Push pending events to the server
   */
  private async pushEvents(
    aggregateType: string,
    profileId: string,
    events: DomainEvent[],
    lastSeenSequence: number | null
  ): Promise<{
    lastKnownCommittedSequence: number | undefined
  }> {
    const accessToken = await this.getAccessToken()
    if (!accessToken) {
      const err = new AppError('No access token available')
      err.code = 'no_access_token'
      err.status = 401
      throw err
    }

    const body: PushRequestBody = {
      last_seen_sequence_number: lastSeenSequence ?? 0,
      events: events.map((event) => {
        const {
          sequence_number,
          device_sequence_number,
          ...data
        } = event.data
        return data
      }),
    }

    const response = await fetchWithAuth<
      SingleResponse<PushResponse>,
      PushRequestBody
    >({
      baseUrl: this.baseUrl,
      accessToken,
      method: 'post',
      endpoint: `/v1/events/${aggregateType}/${profileId}/push`,
      body,
    })

    if (response.error) {
      throw AppError.fromError(
        response.error,
        `Push failed: ${response.error.message}`
      )
    }

    // Mark events as synced with server-assigned sequence numbers
    const lastKnownCommittedSequence =
      await this.eventStore.markEventsSynced(
        response.data?.committed_events ?? []
      )

    // Update last_seen_sequence and keep local ref in sync
    if (lastKnownCommittedSequence) {
      await this.updateEventRef(aggregateType, profileId, {
        last_seen_sequence: lastKnownCommittedSequence,
      })
    } else {
      log.warn('No committed events found in response', response)
    }

    return { lastKnownCommittedSequence }
  }

  /**
   * Get or create domain_event_refs record for tracking sync state
   */
  private async getOrCreateEventRef(
    aggregateType: string,
    profileId: string
  ): Promise<DomainEventRef> {
    const existing = await this.db
      .selectFrom('domain_event_refs')
      .selectAll()
      .where('aggregate_type', '=', aggregateType)
      .where('profile_id', '=', profileId)
      .executeTakeFirst()

    if (existing) {
      return existing
    }

    // Create new ref
    const newRef: DomainEventRef = {
      aggregate_type: aggregateType,
      profile_id: profileId,
      is_active: true,
      sync_state: 'idle',
      message: null,
      last_pull: null,
      last_push: null,
      last_seen_sequence: null,
    }

    await this.db
      .insertInto('domain_event_refs')
      .values(newRef)
      .execute()

    this.notificationBus.notify('domain_event_ref', 'applied')
    return newRef
  }

  /**
   * Update domain_event_refs record
   */
  private async updateEventRef(
    aggregateType: string,
    profileId: string,
    updates: Partial<DomainEventRef>
  ): Promise<void> {
    await this.db
      .updateTable('domain_event_refs')
      .set(updates)
      .where('aggregate_type', '=', aggregateType)
      .where('profile_id', '=', profileId)
      .execute()
    this.notificationBus.notify('domain_event_ref', 'applied')
  }

  /**
   * Update domain_event_refs record
   */
  private async getUpdateEventRefState(
    aggregateType: string,
    profileId: string,
    action: 'pull' | 'push',
    state: DomainEventRefSyncState
  ): Promise<DomainEventRef> {
    const ref = await this.getOrCreateEventRef(
      aggregateType,
      profileId
    )
    let updateState: DomainEventRefSyncState = state
    if (ref.sync_state === 'error') {
      if (state === 'idle' && action === 'push') {
        updateState = state
      } else if (
        state === 'idle' &&
        ref.message === 'connection'
      ) {
        // always reset on a connection error
        updateState = state
      } else {
        updateState = 'error'
      }
    }
    if (updateState === 'idle' && action === 'pull') {
      await this.updateEventRef(aggregateType, profileId, {
        last_pull: new Date().toISOString(),
        sync_state: updateState,
        message: null,
      })
    } else if (updateState === 'idle' && action === 'push') {
      await this.updateEventRef(aggregateType, profileId, {
        last_push: new Date().toISOString(),
        sync_state: updateState,
        message: null,
      })
    } else if (updateState !== ref.sync_state) {
      await this.updateEventRef(aggregateType, profileId, {
        sync_state: updateState,
      })
    }
    return ref
  }

  /**
   * Apply pulled events to local EventStore
   */
  private async applyPulledEvents(
    events: DomainEvent[]
  ): Promise<void> {
    if (events.length === 0) return
    await this.eventStore.mergePulledEvents(events)
  }

  /**
   * True while the server has an access token of ours it will
   * not accept. Nothing syncs automatically until the user has
   * signed in again.
   */
  get isAwaitingReauthentication(): boolean {
    return this.awaitingReauthentication
  }

  /**
   * Stop all automatic syncing because the server rejected our
   * access token.
   *
   * Retrying with the same token only produces more 401s, so we
   * stop the live sync loop and hand control back to the caller
   * through `onUnauthorized`.
   */
  private stopForReauthentication(
    aggregateType: string,
    profileId: string
  ): void {
    if (this.awaitingReauthentication) return
    this.awaitingReauthentication = true
    log.info('Sync stopped, waiting for reauthentication')
    void this.stopLiveSync()
    this.onUnauthorized?.(aggregateType, profileId)
  }

  /**
   * Allow automatic syncing again after the user has signed in.
   *
   * The caller is responsible for starting a new sync — this
   * only lifts the block.
   */
  resumeAfterReauthentication(): void {
    if (!this.awaitingReauthentication) return
    log.info('Reauthenticated, sync allowed again')
    this.awaitingReauthentication = false
  }

  /**
   * Record the failure on the ref row and, when the server
   * rejected our access token, stop automatic syncing
   */
  private async handleSyncError(
    aggregateType: string,
    profileId: string,
    error: AppError
  ): Promise<void> {
    await this.updateEventRef(aggregateType, profileId, {
      sync_state: 'error',
      message: error.code,
    })
    if (error.isUnauthorized()) {
      this.stopForReauthentication(aggregateType, profileId)
    }
  }

  /**
   * Exponential backoff delay
   */
  private async delay(attempt: number): Promise<void> {
    const baseDelay = 1000 // 1 second
    const maxDelay = 32000 // 32 seconds
    const ms = Math.min(
      baseDelay * Math.pow(2, attempt),
      maxDelay
    )
    await delay(delayJitter(ms, 0.5))
  }

  /**
   * Pull all events from server with pagination
   */
  async pullAll(
    aggregateType: string,
    profileId: string,
    live: boolean = false
  ): Promise<void> {
    const ref = await this.getUpdateEventRefState(
      aggregateType,
      profileId,
      'pull',
      live ? 'live' : 'syncing'
    )

    try {
      let cursor = ref.last_seen_sequence ?? 0
      let hasMore = true
      let remoteSequence: number | null = null

      if (!live && this.onPullProgress) {
        const status = await this.fetchRemoteStatus(
          aggregateType,
          profileId
        )
        remoteSequence = status?.latest_sequence_number ?? null
        this.onPullProgress({
          aggregateType,
          profileId,
          syncedSequence: cursor,
          remoteSequence,
        })
      }

      while (hasMore) {
        const { error, events, pagination } =
          await this.pullEvents(
            aggregateType,
            profileId,
            cursor,
            50,
            live
          )

        if (error) {
          // exit early from the while loop, caught below at the bottom of pullAll
          throw error
        }

        await this.applyPulledEvents(events ?? [])

        hasMore = pagination.has_more
        if (hasMore && pagination.next_cursor !== null) {
          cursor = pagination.next_cursor
        }

        // Update last_seen_sequence after each batch
        if (isArrayWithLength(events)) {
          const lastEvent = events[events.length - 1]
          if (lastEvent.sequence_number !== undefined) {
            await this.updateEventRef(aggregateType, profileId, {
              last_seen_sequence: lastEvent.sequence_number,
              message: null,
            })
            if (!live) {
              this.onPullProgress?.({
                aggregateType,
                profileId,
                syncedSequence: lastEvent.sequence_number,
                remoteSequence,
              })
            }
          }
        } else {
          await this.updateEventRef(aggregateType, profileId, {
            sync_state: live ? 'live' : 'idle',
            message: null,
          })
        }
      }

      // Update last_pull timestamp
      await this.getUpdateEventRefState(
        aggregateType,
        profileId,
        'pull',
        'idle'
      )
    } catch (error) {
      await this.handleSyncError(
        aggregateType,
        profileId,
        AppError.fromError(error)
      )
      throw error
    }
  }

  /**
   * Push pending events to server with exponential backoff
   */
  async pushPending(
    aggregateType: string,
    profileId: string,
    maxRetries: number = 5
  ): Promise<void> {
    if (this.isPushing) return
    this.isPushing = true

    try {
      let ref = await this.getUpdateEventRefState(
        aggregateType,
        profileId,
        'push',
        'syncing'
      )

      if (
        ref.sync_state === 'error' &&
        ref.message === 'forbidden_event_pushed'
      ) {
        log.warn(
          'Will not push until forbidden event state removed'
        )
        const error = new AppError(
          'Will not push until forbidden event state removed'
        )
        error.code = ref.message
        error.status = 403
        throw error
      }

      await retry(
        async () => {
          // Get pending events
          const pendingEvents =
            await this.eventStore.getPendingEvents(
              aggregateType,
              profileId
            )

          if (isArrayWithLength(pendingEvents)) {
            // Push in batches of 10
            const batches = chunk(pendingEvents, 10)
            for (const batch of batches) {
              const { lastKnownCommittedSequence } =
                await this.pushEvents(
                  aggregateType,
                  profileId,
                  batch,
                  ref.last_seen_sequence
                )
              if (lastKnownCommittedSequence) {
                ref = {
                  ...ref,
                  last_seen_sequence: lastKnownCommittedSequence,
                }
              }
            }
          }

          // Update last_push timestamp
          await this.getUpdateEventRefState(
            aggregateType,
            profileId,
            'push',
            'idle'
          )
          return // Success
        },
        {
          maxAttempts: maxRetries,
          isRetryable: async (error, attempt) => {
            const lastError = AppError.fromError(error)

            // Check if it's a sequence mismatch (409 Conflict)
            if (lastError.isConflictError()) {
              log.warn(
                `Push conflict (409), pulling to catch up (attempt ${attempt})`,
                error
              )

              // Pull to catch up with the server, then retry
              try {
                await this.pullAll(aggregateType, profileId)
              } catch (pullError) {
                throw AppError.fromError(
                  pullError,
                  'Failed to pull during conflict recovery'
                )
              }

              // Re-read ref for updated last_seen_sequence
              // Restore sync state for retry
              ref = await this.getUpdateEventRefState(
                aggregateType,
                profileId,
                'push',
                'syncing'
              )

              // Don't count conflict recovery against regular retries
              return true
            }

            if (error instanceof AppError) {
              if (error.isUnauthorized()) {
                return false
              }
              if (error.isForbiddenEventPushed()) {
                captureException(error)
                return false
              }
            }

            return true
          },
        }
      )
    } catch (error) {
      // All retries failed
      const err = AppError.fromError(
        error,
        `Failed to push events after ${maxRetries} attempts}`
      )
      await this.handleSyncError(aggregateType, profileId, err)
      throw err
    } finally {
      this.isPushing = false
    }
  }

  /**
   * Perform complete pull-then-push sync flow
   */
  async sync(
    aggregateType: string,
    profileId: string
  ): Promise<void> {
    try {
      // Step 1: Pull events from server
      await this.pullAll(aggregateType, profileId)

      // Step 2: Push pending events to server
      await this.pushPending(aggregateType, profileId)
    } catch (err) {
      const error = AppError.fromError(err, 'Sync failed')
      error.log()
      throw error
    }
  }

  /**
   * Pull events use live mode which will instruct the server to use a long-polling mechanism to wait for new events
   */
  private async pullLiveSync(
    aggregateType: string,
    profileId: string
  ) {
    let failures = 0
    while (
      this.liveSyncEnabled &&
      !this.awaitingReauthentication
    ) {
      try {
        await this.pullAll(aggregateType, profileId, true)
        // reset attempt after successful pull
        failures = 0
      } catch (err) {
        failures++
        const error = AppError.fromError(
          err,
          'Live sync pull failed'
        )
        error.log()
      }
      if (this.liveSyncEnabled) {
        await this.delay(failures)
      }
    }
  }

  async startLiveSync(aggregateType: string, profileId: string) {
    if (this.awaitingReauthentication) {
      log.info(
        'Not starting live sync, waiting for reauthentication'
      )
      return
    }
    this.liveSyncEnabled = true
    this.onDomainEvent = () => {
      if (this.awaitingReauthentication) return
      this.pushPending(aggregateType, profileId).catch(
        (error) => {
          log.error('Push during live sync failed:', error)
        }
      )
    }
    this.notificationBus.listen(
      'domain_event',
      this.onDomainEvent
    )
    await this.pullLiveSync(aggregateType, profileId)
  }

  async stopLiveSync() {
    this.liveSyncEnabled = false
    if (this.abortPull) {
      this.abortPull.abort()
    }
    if (this.onDomainEvent) {
      this.notificationBus.unlisten(
        'domain_event',
        this.onDomainEvent
      )
    }
    this.onDomainEvent = undefined
  }

  /**
   * Get sync status for an aggregate/profile
   */
  async getSyncStatus(
    aggregateType: string,
    profileId: string
  ): Promise<DomainEventRef | null> {
    return await this.db
      .selectFrom('domain_event_refs')
      .selectAll()
      .where('aggregate_type', '=', aggregateType)
      .where('profile_id', '=', profileId)
      .executeTakeFirst()
      .then((result) => result ?? null)
  }
}
